import React from 'react';
import {
  RiCalendarLine,
  RiCheckboxCircleLine,
  RiDeleteBinLine,
  RiEditLine,
  RiErrorWarningLine,
  RiTimeLine,
} from 'react-icons/ri';
import { FiCreditCard } from 'react-icons/fi';

const formatMontant = (montant) =>
  Number(montant || 0).toLocaleString('fr-FR');

const formatDate = (iso) => {
  if (!iso) return ':';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const badgeUrgence = (obligation, estPayee, enRetard) => {
  if (estPayee) {
    return (
      <span className="fisc-badge fisc-badge-success">
        <RiCheckboxCircleLine /> Payé / Acquitté
      </span>
    );
  }
  if (enRetard) {
    return (
      <span className="fisc-badge fisc-badge-danger fisc-pulse">
        <RiErrorWarningLine /> En retard (pénalités encourues)
      </span>
    );
  }
  const jours = obligation.jours_restants;
  if (jours === null || jours === undefined) {
    return <span className="fisc-badge fisc-badge-warning">En attente</span>;
  }
  if (jours <= 5) {
    return (
      <span className="fisc-badge fisc-badge-warning">
        <RiTimeLine /> Critique ({jours} jour{jours > 1 ? 's' : ''})
      </span>
    );
  }
  if (jours <= 15) {
    return (
      <span className="fisc-badge fisc-badge-info">
        <RiTimeLine /> Dans {jours} jours
      </span>
    );
  }
  return (
    <span className="fisc-badge fisc-badge-light">
      <RiCalendarLine /> Dans {jours} jours
    </span>
  );
};

const ObligationCard = ({ obligation, canEdit, onPayer, onDeclarer, onEdit, onDelete }) => {
  const estPayee = obligation.statut_effectif === 'PAYE';
  const enRetard = obligation.statut_effectif === 'EN_RETARD';

  return (
    <div className={`fisc-row ${enRetard ? 'fisc-row-retard' : ''}`}>
      <div className="fisc-row-main">
        <div className="fisc-row-badges">
          {obligation.code && <span className="fisc-code">{obligation.code}</span>}
          <h5 className="fisc-nom">{obligation.nom}</h5>
          {badgeUrgence(obligation, estPayee, enRetard)}
          {obligation.statut === 'DECLARE' && !estPayee && (
            <span className="fisc-badge fisc-badge-info">Déclaré</span>
          )}
          {obligation.is_recurrente && (
            <span
              className="fisc-badge fisc-badge-light"
              title="Échéance régénérée automatiquement après paiement"
            >
              Récurrente
            </span>
          )}
        </div>

        {obligation.description && (
          <p className="fisc-description">{obligation.description}</p>
        )}

        <div className="fisc-meta">
          {obligation.periode && (
            <span>
              Période : <strong>{obligation.periode}</strong>
            </span>
          )}
          <span>
            Date butoir :{' '}
            <strong className="fisc-date-butoir">
              {formatDate(obligation.date_echeance)}
            </strong>
          </span>
          <span className="fisc-badge fisc-badge-light">
            {obligation.periodicite_label || obligation.periodicite}
          </span>
        </div>

        {obligation.risque_penalite && !estPayee && (
          <div className={`fisc-penalite ${enRetard ? 'fisc-penalite-danger' : ''}`}>
            <RiErrorWarningLine style={{ marginRight: 4 }} />
            {obligation.risque_penalite}
          </div>
        )}
      </div>

      <div className="fisc-row-side">
        <div className="fisc-row-montant">
          <span>Montant exigible</span>
          <strong>{formatMontant(obligation.montant)} FCFA</strong>
        </div>

        {estPayee ? (
          <div className="fisc-paye-info">
            <RiCheckboxCircleLine /> Réglée le {formatDate(obligation.date_paiement)}
            {obligation.compte_paiement_nom && ` (${obligation.compte_paiement_nom})`}
            {obligation.piece_journal && ` : ${obligation.piece_journal}`}
          </div>
        ) : (
          canEdit && (
            <div className="fisc-row-actions">
              <button
                type="button"
                className="fisc-btn-payer"
                onClick={() => onPayer(obligation)}
              >
                <FiCreditCard /> Déclarer &amp; Payer
              </button>
              {obligation.statut === 'EN_ATTENTE' && (
                <button
                  type="button"
                  className="fisc-btn-outline"
                  onClick={() => onDeclarer(obligation)}
                >
                  Marquer déclaré
                </button>
              )}
              <button
                type="button"
                className="fisc-btn-icon"
                title="Modifier l'obligation"
                onClick={() => onEdit(obligation)}
              >
                <RiEditLine />
              </button>
              <button
                type="button"
                className="fisc-btn-icon fisc-btn-icon-danger"
                title="Supprimer l'obligation"
                onClick={() => onDelete(obligation)}
              >
                <RiDeleteBinLine />
              </button>
            </div>
          )
        )}
        {estPayee && obligation.sortie && (
          <span className="fisc-badge fisc-badge-light" title="Dépense liée dans la trésorerie">
            Sortie #{obligation.sortie}
          </span>
        )}
      </div>
    </div>
  );
};

export default ObligationCard;
